import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { apiService, type Lesson } from "@/services/api.service";
import { Input } from "@/components/ui/input";
import { lessonSearchParams } from "@/lib/lesson-search";

const DEBOUNCE_MS = 250;

type Props = {
  value: string;
  disabled?: boolean;
  className?: string;
  onTextChange: (value: string) => void;
  onSelect: (lesson: Lesson) => void;
};

/**
 * Lesson name field that doubles as a search box.
 *
 * Searches through apiService.getLessonsPage without a `status` filter, so
 * drafts come back alongside published lessons — a curriculum slot is usually
 * filled before its lesson is published. Picking a result fills the lesson ID
 * next door, which is otherwise typed by hand.
 */
export function LessonSearchInput({
  value,
  disabled,
  className,
  onTextChange,
  onSelect,
}: Props) {
  const [filters, setFilters] = useState<FilterValues>({});
  const [options, setOptions] = useState<{ organizations: { id: string; name: string }[]; teachers: { id: number; name: string }[] }>({ organizations: [], teachers: [] });
  useEffect(() => {
    if (disabled) return;
    const controller = new AbortController();
    Promise.all([
      fetch("/api/internal/admin/organizations", { credentials: "include", signal: controller.signal }).then((r) => r.ok ? r.json() : []),
      fetch("/api/proxy/lessons/search-options", { credentials: "include", signal: controller.signal }).then((r) => r.ok ? r.json() : { teachers: [] }),
    ]).then(([organizations, result]) => setOptions({ organizations, teachers: result.teachers })).catch(() => {});
    return () => controller.abort();
  }, [disabled]);
  const [results, setResults] = useState<Lesson[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [query, setQuery] = useState("");
  const listId = useId();
  // getLessonsPage takes no AbortSignal, so stale responses are dropped by
  // sequence number instead — otherwise a slow early keystroke can land last
  // and overwrite the results for what was actually typed.
  const latestRequest = useRef(0);
  // The query a selection wrote, so its own state change does not search for
  // the name that was just picked. Compared rather than consumed: a one-shot
  // flag stays armed when the selected name already equals the query, and then
  // swallows the next real edit.
  const suppressedQuery = useRef<string | null>(null);

  const search = useCallback((term: string) => {
    const requestId = ++latestRequest.current;
    return apiService
      .getLessonsPage({ ...lessonSearchParams(term), organizationId: filters.organization_id || undefined, teacherId: filters.teacher_id ? Number(filters.teacher_id) : undefined })
      .then((page) => {
        if (requestId !== latestRequest.current) return;
        setResults(page.items ?? []);
        setActiveIndex(-1);
        setOpen(true);
      })
      .catch(() => {
        if (requestId !== latestRequest.current) return;
        setResults([]);
        setOpen(false);
      });
  }, [filters]);

  useEffect(() => {
    // Bumped for every query change, not just the ones that reach a request,
    // so anything already in flight is invalidated the moment the text moves
    // on — including when it is cleared, which issues no request of its own.
    ++latestRequest.current;
    if (query === suppressedQuery.current) return;
    const term = query.trim();
    const timer = window.setTimeout(() => {
      if (!term && !Object.values(filters).some(Boolean)) {
        setResults([]);
        setOpen(false);
        return;
      }
      void search(term);
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query, search, filters]);

  const choose = (lesson: Lesson) => {
    suppressedQuery.current = lesson.name;
    setQuery(lesson.name);
    setOpen(false);
    setActiveIndex(-1);
    onSelect(lesson);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        const next = current + step;
        if (next < 0) return results.length - 1;
        if (next >= results.length) return 0;
        return next;
      });
      return;
    }
    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      choose(results[activeIndex]!);
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="relative min-w-56 flex-1 flex items-center gap-2">
      <Input
        aria-label="Lesson name"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
        }
        autoComplete="off"
        value={value}
        placeholder="Search lessons by name"
        disabled={disabled}
        className={className}
        onChange={(event) => {
          // Typing is intent to search, so a previous selection stops
          // suppressing anything — including retyping the same name.
          suppressedQuery.current = null;
          onTextChange(event.target.value);
          setQuery(event.target.value);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (results.length > 0) {
            setOpen(true);
            return;
          }
          // A saved lesson arrives with its name already in the field, so
          // there is nothing left to type: offer matches for what is there
          // instead of waiting for a keystroke.
          const term = value.trim();
          if (term) void search(term);
        }}
        // Blur fires before a click on a result registers, so closing is
        // deferred rather than immediate.
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
      />
      {!disabled && <div className="shrink-0"><AdvancedOptions values={filters} onChange={(values) => { suppressedQuery.current = null; setFilters(values); }} fields={[
        { key: "organization_id", label: "Organization", options: options.organizations.map((org) => ({ value: org.id, label: org.name })) },
        { key: "teacher_id", label: "Teacher owner", options: options.teachers.map((teacher) => ({ value: String(teacher.id), label: `${teacher.name} (#${teacher.id})` })) },
      ]} /></div>}
      {open && results.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-full left-0 z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-input bg-popover p-1 shadow-md"
        >
          {results.map((lesson, index) => (
            <li key={lesson.lesson_id}>
              <button
                type="button"
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm ${
                  index === activeIndex ? "bg-accent" : "hover:bg-accent/60"
                }`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(lesson)}
              >
                {lesson.is_published ? null : (
                  <span className="shrink-0 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-amber-900 uppercase">
                    Draft
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate">{lesson.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  #{lesson.lesson_id}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
